import { useState, useCallback, useEffect, useMemo } from "react";
import { Drawer } from "./Drawer";
import { Button } from "../atoms/Button";
import { Input } from "../atoms/Input";
import { InputWithDropdown } from "../molecules/InputWithDropdown";
import { Building } from "lucide-react";
import type { LegalEntityFormData } from "../../types/legalEntity";
import {
  fetchLocationStates,
  fetchLocationCities,
  fetchLocationZips,
  fetchLocationCounty,
} from "../../services/locationReferenceService";
import { validateAddLegalEntityForm } from "../../utils/validation/legalEntityValidation";
import { resolveLocationFieldResets } from "../../utils/validation/locationConsistency";

type AddLegalEntityDrawerProps = {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSave: (formData: LegalEntityFormData) => Promise<void>;
};

const emptyForm: LegalEntityFormData = {
  name: "",
  title: "",
  company: "",
  streetAddress: "",
  city: "",
  state: "",
  county: "",
  zip: "",
  phone: "",
  email: "",
};

export const AddLegalEntityDrawer = ({ isOpen, onClose, onSave }: AddLegalEntityDrawerProps) => {
  const [formData, setFormData] = useState<LegalEntityFormData>(emptyForm);
  /** After first Save attempt, show validation messages (derived from formData). */
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [locationStates, setLocationStates] = useState<string[]>([]);
  const [locationCities, setLocationCities] = useState<string[]>([]);
  const [locationZips, setLocationZips] = useState<string[]>([]);

  const formErrors = useMemo(() => validateAddLegalEntityForm(formData), [formData]);
  const hasValidationErrors = Object.keys(formErrors).length > 0;
  const [touched, setTouched] = useState<Partial<Record<keyof LegalEntityFormData, boolean>>>({});

  const visibleErrors = useMemo(() => {
    if (submitAttempted) {
      return formErrors;
    }
    const v: typeof formErrors = {};
    (["company", "streetAddress", "city", "state", "zip"] as const).forEach((k) => {
      if (touched[k] && formErrors[k]) {
        v[k] = formErrors[k];
      }
    });
    return v;
  }, [formErrors, submitAttempted, touched]);

  const resetForm = useCallback(() => {
    setFormData(emptyForm);
    setSubmitAttempted(false);
    setIsSubmitting(false);
    setSubmitError(null);
    setLocationCities([]);
    setLocationZips([]);
    setTouched({});
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const states = await fetchLocationStates();
        if (!cancelled) {
          setLocationStates(states);
        }
      } catch {
        if (!cancelled) {
          setLocationStates([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    const st = formData.state.trim();
    if (!st) {
      setLocationCities([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const cities = await fetchLocationCities(st);
        if (!cancelled) {
          setLocationCities(cities);
        }
      } catch {
        if (!cancelled) {
          setLocationCities([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [formData.state]);

  useEffect(() => {
    const st = formData.state.trim();
    const cy = formData.city.trim();
    if (!st || !cy) {
      setLocationZips([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const zips = await fetchLocationZips(st, cy);
        if (!cancelled) {
          setLocationZips(zips);
        }
      } catch {
        if (!cancelled) {
          setLocationZips([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [formData.state, formData.city]);

  useEffect(() => {
    const st = formData.state.trim();
    const cy = formData.city.trim();
    const z = formData.zip.trim();
    if (!st || !cy || !z) {
      setFormData((prev) => (prev.county ? { ...prev, county: "" } : prev));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const county = await fetchLocationCounty(st, cy, z);
        if (!cancelled) {
          setFormData((prev) => ({ ...prev, county: county ?? "" }));
        }
      } catch {
        if (!cancelled) {
          setFormData((prev) => ({ ...prev, county: "" }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [formData.state, formData.city, formData.zip]);

  const handleSubmit = useCallback(async () => {
    setSubmitAttempted(true);
    const validationErrors = validateAddLegalEntityForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(null);
      await onSave(formData);
      resetForm();
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to create legal entity";
      setSubmitError(message);
      setIsSubmitting(false);
    }
  }, [formData, onSave, onClose, resetForm]);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Legal Entity"
      className="px-0"
      headerIconContainerClassName="bg-[#009ce5] text-white"
      headerIcon={<Building className="h-5 w-5" />}
    >
      <div className="flex h-full min-h-0 flex-col">
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-6">
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label
                htmlFor="legal-entity-company"
                className="block text-sm font-medium text-gray-700"
              >
                Company <span className="text-status-error">*</span>
              </label>
              <Input
                id="legal-entity-company"
                value={formData.company}
                onChange={(e) => setFormData((prev) => ({ ...prev, company: e.target.value }))}
                onBlur={() => setTouched((prev) => ({ ...prev, company: true }))}
                placeholder="Enter company"
                aria-invalid={!!visibleErrors.company}
              />
              {visibleErrors.company && <p className="text-sm text-status-error">{visibleErrors.company}</p>}
            </div>

            <div className="space-y-1.5">
              <label
                htmlFor="legal-entity-street"
                className="block text-sm font-medium text-gray-700"
              >
                Street Address <span className="text-status-error">*</span>
              </label>
              <Input
                id="legal-entity-street"
                value={formData.streetAddress}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, streetAddress: e.target.value }))
                }
                onBlur={() => setTouched((prev) => ({ ...prev, streetAddress: true }))}
                placeholder="Enter street address"
                aria-invalid={!!visibleErrors.streetAddress}
              />
              {visibleErrors.streetAddress && (
                <p className="text-sm text-status-error">{visibleErrors.streetAddress}</p>
              )}
            </div>

            <InputWithDropdown
              id="legal-entity-state"
              label="State"
              required
              options={locationStates}
              value={formData.state}
              error={visibleErrors.state}
              placeholder="Enter state"
              onChange={(state) => {
                const resets = resolveLocationFieldResets(
                  formData.state,
                  state,
                  formData.city,
                  formData.city
                );
                setFormData((prev) => ({
                  ...prev,
                  state,
                  city: resets.resetCity ? "" : prev.city,
                  zip: resets.resetZip ? "" : prev.zip,
                  county: "",
                }));
                setLocationZips([]);
              }}
              onBlur={() => setTouched((prev) => ({ ...prev, state: true }))}
            />

            <InputWithDropdown
              id="legal-entity-city"
              label="City"
              required
              options={locationCities}
              value={formData.city}
              error={visibleErrors.city}
              placeholder={formData.state.trim() ? "Enter city" : "Enter state first"}
              disabled={!formData.state.trim()}
              onChange={(city) => {
                const resets = resolveLocationFieldResets(
                  formData.state,
                  formData.state,
                  formData.city,
                  city
                );
                setFormData((prev) => ({
                  ...prev,
                  city,
                  zip: resets.resetZip ? "" : prev.zip,
                  county: "",
                }));
                setLocationZips([]);
              }}
              onBlur={() => setTouched((prev) => ({ ...prev, city: true }))}
            />

            <InputWithDropdown
              id="legal-entity-zip"
              label="Zip"
              required
              type="tel"
              inputMode="numeric"
              maxLength={5}
              pattern="[0-9]*"
              options={locationZips}
              value={formData.zip}
              error={visibleErrors.zip}
              placeholder={formData.city.trim() ? "Enter ZIP" : "Enter city first"}
              disabled={!formData.city.trim()}
              onChange={(zip) => {
                const normalizedZip = zip.replace(/\D/g, "").slice(0, 5);
                setFormData((prev) => ({ ...prev, zip: normalizedZip, county: "" }));
              }}
              onBlur={() => setTouched((prev) => ({ ...prev, zip: true }))}
            />

            {submitError && (
              <div className="rounded-lg border border-status-error bg-red-50 px-4 py-3 text-sm text-status-error">
                {submitError}
              </div>
            )}
          </div>
        </div>

        <div className="shrink-0 border-t border-gray-200 px-6 py-6">
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || (submitAttempted && hasValidationErrors)}
            >
              {isSubmitting ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </div>
    </Drawer>
  );
};
